import store from '../../../store/store';
import { searchFilterFacets } from '../../search/interfaces/search-filter.interface';
import {
  DISCIPLINE_PRODUCT_TAG_PATH,
  PARAM_DISCIPLINE,
  removeQueryStringFromUrl,
} from '../../search/thunks/search.thunk';
import { getSearchV2ApiCall } from '../api/search-v2.apis';
import { selectV2Request } from '../store/search-v2.selectors';
import { onFailure, onSuccess, setLoading } from '../store/search-v2.store';

export interface PerformSearchV2Params {
  ownerId?: string;
  searchTerms?: string;
  catalog?: string;
  pageLength?: number;
  hits?: boolean;
  start?: number;
  facets?: boolean;
  store?: string;
  fieldValueFilter?: { [key: string]: Array<string> };
  addFacetName?: string;
  addFacetValue?: string;
  removeFacetName?: string;
  removeFacetValue?: string;
  excludeFacetName?: string;
  //filter?: Filter; // For plp flow we need to pass programcode as filter
  sortBy?: string; // For plp flow sortBy option is passed for sorting the results
  clearFilter?: boolean; // if this flag is true we are clearing old filter before performing the search
  clearSort?: boolean; // if this flag is true we are clearing old sorting paramters before performing the search
  isPlpSearch?: boolean;
  isCatalogSet?: boolean;
  categoryId?: string;
}

export const performV2Search =
  (params: PerformSearchV2Params = {}) =>
  async (dispatch: Function, getState: Function) => {
    const request = getSearchV2Request(params, getState());

    dispatch(setLoading(request));

    try {
      const searchData = await getSearchV2ApiCall(request);

      // Request-identity guard: `setLoading(request)` stored `request` by reference in
      // `state.searchV2State.search.request`. If a newer dispatch replaced it while we
      // were awaiting, our response is stale — do not touch Redux (data, isLoading,
      // isFailure). Same pattern as `performSearch` in `../../search/thunks/search.thunk.ts`.
      if (selectV2Request(getState()) !== request) {
        return undefined;
      }

      if (searchData) {
        dispatch(onSuccess(searchData));
        return searchData;
      }
      dispatch(onFailure());
      return undefined;
    } catch (error) {
      // A late-arriving rejection from a superseded request must not clobber the
      // loading flag of the currently in-flight request.
      if (selectV2Request(getState()) === request) {
        dispatch(onFailure());
      }
      return undefined;
    }
  };

export function getSearchV2Request(params, state) {
  let oldRequest: PerformSearchV2Params = { ...selectV2Request(state) };

  if (!oldRequest) {
    oldRequest = {
      hits: true,
      searchTerms: '',
      catalog: 'UNIFIED',
      pageLength: 10,
      start: 0,
      facets: true,
    };
  }

  if (params.clearSort) {
    oldRequest.sortBy = undefined;
  }

  if (params.sortBy) {
    oldRequest.sortBy = params.sortBy;
  }

  oldRequest.addFacetName = undefined;
  oldRequest.addFacetValue = undefined;
  oldRequest.removeFacetName = undefined;
  oldRequest.removeFacetValue = undefined;

  const request: PerformSearchV2Params = { ...JSON.parse(JSON.stringify(oldRequest)), ...params };

  if (params.isPlpSearch) {
    request.isPlpSearch = params.isPlpSearch;
  }

  if (!request.fieldValueFilter) {
    request.fieldValueFilter = {};
  }

  if (params.addFacetName && !request.fieldValueFilter[params.addFacetName]) {
    request.fieldValueFilter[params.addFacetName] = [];
  }

  if (params.addFacetName && params.addFacetValue) {
    request.fieldValueFilter[params.addFacetName] = request.fieldValueFilter[params.addFacetName].filter((facet) => {
      if (params.addFacetValue) {
        return !params.addFacetValue.startsWith(facet) && !facet.startsWith(params.addFacetValue);
      }
      return true;
    });

    request.fieldValueFilter[params.addFacetName].push(params.addFacetValue);
  }

  if (params.removeFacetName && !request.fieldValueFilter[params.removeFacetName]) {
    request.fieldValueFilter[params.removeFacetName] = [];
  }

  if (params.removeFacetName && params.removeFacetValue) {
    request.fieldValueFilter[params.removeFacetName] = request.fieldValueFilter[params.removeFacetName].filter(
      (value: string) => value != params.removeFacetValue
    );

    if (request.fieldValueFilter[params.removeFacetName].length === 0) {
      delete request.fieldValueFilter[params.removeFacetName];
    }
  }

  let page = request.start;
  if (page && page > 1) {
    const pageNo = (page - 1) / request.pageLength! + 1;
    if (Number.isInteger(pageNo)) {
      page = pageNo;
    }
  }
  updateSearchV2Url({ searchQuery: request.searchTerms, page: page, fieldValueFilter: request.fieldValueFilter });
  if (!request.pageLength) {
    request.pageLength = 10;
  }
  return request;
}

/**
 * adding/modifying a query search string of page url without reloading the page
 * @param param
 */
export function updateSearchV2Url({ searchQuery = '', page = 1, fieldValueFilter = {} }) {
  const url = new URL(window.location.href);
  url.searchParams.set(PARAM_SEARCH_QUERY, searchQuery);
  url.searchParams.set(PARAM_PAGE, page.toString());

  const filterParamsUrl = buildUrlWithFilterParams(url, fieldValueFilter);

  const updatedUrl = removeQueryStringFromUrl(filterParamsUrl, PARAM_DISCIPLINE);
  window.history.pushState({ path: updatedUrl }, '', updatedUrl);
}

/**
 * set search query params for multiple search filters
 */
export function buildUrlWithFilterParams(url: URL, fieldValueFilter = {}) {
  searchV2FilterFacets.forEach((facet) => {
    url.searchParams.delete(facet);
  });

  searchFilterFacets.forEach((facet) => {
    url.searchParams.delete(facet);
  });

  if (Object.keys(fieldValueFilter).length !== 0) {
    for (const field in fieldValueFilter) {
      if (fieldValueFilter.hasOwnProperty(field)) {
        url.searchParams.set(field, fieldValueFilter[field]);
      }
    }
  }

  return url;
}

/**
 * This function is return the request object which is created from query search parameter
 * @returns value of search param
 */
export function getSearchRequestFromUrl(pageSize = 10, searchUrl = '') {
  const urlParams = new URLSearchParams(searchUrl || location.search);
  const searchQuery = urlParams.get(PARAM_SEARCH_QUERY);
  // const sortby = urlParams.get(PARAM_SORT_BY);
  // const catalog = urlParams.get(PARAM_CATALOG);
  let page = urlParams.get(PARAM_PAGE) || '1';
  if (page && page !== '1') {
    page = ((JSON.parse(page) - 1) * pageSize + 1).toString();
  }

  const discipline = urlParams.get(PARAM_DISCIPLINE);
  let fieldValueFilter = {};
  if (discipline) {
    fieldValueFilter = { [DISCIPLINE_PRODUCT_TAG_PATH]: [discipline] };
  }

  searchV2FilterFacets.forEach((facet) => {
    const fieldValues = urlParams.get(facet);
    if (fieldValues) {
      fieldValueFilter[facet] = fieldValues.split(',');
    }
  });

  const searchObj = {
    searchQuery: searchQuery! || '',
    page: JSON.parse(page) || 1,
    fieldValueFilter: fieldValueFilter!,
  };
  return searchObj;
}

export const PARAM_SEARCH_QUERY = 'searchQuery';
export const PARAM_PAGE = 'page';

export const searchV2FilterFacets = ['learning_level', 'user_type', 'product', 'created_by_division'];
